/**
 * Centralized card position resolution.
 *
 * This module is the SINGLE SOURCE OF TRUTH for where a card should be rendered.
 * Instead of multiple systems (animation, drag, assignCardCoordinates) all
 * mutating card.x/y directly, they write to separate overlay maps here.
 *
 * Resolution priority (highest wins):
 *   1. Drag overlay  — card is being dragged by the user
 *   2. Animation overlay — card is mid-animation
 *   3. card.x / card.y — the logical "home" position from assignCardCoordinates
 *
 * This eliminates the bounce bug: card.x/y are always the correct final position,
 * and transient states (drag, animation) are layered on top without mutation.
 */

import { Card } from '../Models/Card'

interface CardPosition {
    x: number
    y: number
}

// ─── Overlay Maps ───────────────────────────────────────────────────
// Use the Card object reference as key (WeakMap) so entries are GC'd
// when cards are discarded.

const animationOverlay = new Map<Card, CardPosition>()
const dragOverlay = new Map<Card, CardPosition>()

/** True when a card currently has an animation overlay entry. */
export function isCardAnimating(card: Card): boolean {
    return animationOverlay.has(card)
}

// ─── Public API: Write ──────────────────────────────────────────────

/** Set the animation-interpolated position for a card. Called by animation tick(). */
export function setAnimationPosition(card: Card, x: number, y: number): void {
    animationOverlay.set(card, { x, y })
}

/** Remove the animation overlay for a card. Called when animation completes. */
export function clearAnimationPosition(card: Card): void {
    animationOverlay.delete(card)
}

/** Remove ALL animation overlays. Called when all animations finish or are cancelled. */
export function clearAllAnimationPositions(): void {
    animationOverlay.clear()
}

/** Set the drag position for a card. Called by canvasTouchMove. */
export function setDragPosition(card: Card, x: number, y: number): void {
    dragOverlay.set(card, { x, y })
}

/** Remove ALL drag overlays. Called when drag ends. */
export function clearAllDragPositions(): void {
    dragOverlay.clear()
}

// ─── Public API: Read ───────────────────────────────────────────────

/**
 * Resolve the current render position for a card.
 * This is the ONLY function that rendering and hit-detection should use.
 */
export function getCardPosition(card: Card): CardPosition {
    // Priority 1: Drag
    const dragPos = dragOverlay.get(card)
    if (dragPos) return dragPos

    // Priority 2: Animation
    const animPos = animationOverlay.get(card)
    if (animPos) return animPos

    // Priority 3: Logical home position
    return { x: card.x, y: card.y }
}
